import { useState, useEffect, useRef, useCallback } from 'react';
import { logger } from 'src/utils/logger';
import { saveMessages, loadMessages, clearAll } from './sessionStorageService';
import { DEFAULT_SUGGESTIONS, generateMessageId } from './constants';
import { finalizeStreamingMessages } from './messageUtils';
import { useDarkMode } from './useDarkMode';
import { useResizeLogic } from './useResizeLogic';
import { useAgentSession } from './useAgentSession';

export interface Message {
	id: string;
	text: string;
	sender: 'user' | 'bot';
	timestamp: number;
	isTyping?: boolean;
	fullContent?: string;
}

export interface ConversationHistory {
	id: string;
	title: string;
	timestamp: number;
	messages: Message[];
}

export const useChatbotLogic = () => {
	const [isIconHovered, setIsIconHovered] = useState(false);
	const [isOpen, setIsOpen] = useState(() => {
		const flag = sessionStorage.getItem('chatbot-open-on-navigate');
		if (flag !== null) {
			sessionStorage.removeItem('chatbot-open-on-navigate');
			return flag === 'true';
		}
		return false;
	});
	const [currentView, setCurrentView] = useState<'chat' | 'history' | 'help'>('chat');
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [messages, setMessages] = useState<Message[]>([]);
	const [inputValue, setInputValue] = useState('');
	const [isTyping, setIsTyping] = useState(false);
	const [showSuggestions, setShowSuggestions] = useState(true);
	const [conversationHistory, setConversationHistory] = useState<ConversationHistory[]>([]);
	const [showScrollToBottom, setShowScrollToBottom] = useState(false);

	const chatPanelRef = useRef<HTMLDivElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const messageListRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);

	const isDarkMode = useDarkMode();
	const {
		buttonPosition,
		isDragging,
		panelWidth,
		panelPositionStyle,
		handleMouseDown,
		handlePanelResize,
		handlePanelResizeEnd,
	} = useResizeLogic();
	const { username, userPhotoUrl, sendToAgent, resetSession, stopResponse } = useAgentSession(setMessages, setIsTyping);

	useEffect(() => {
		const restoredMessages = loadMessages();
		if (restoredMessages && restoredMessages.length > 0) {
			const hasUserMessage = restoredMessages.some(m => m.sender === 'user');
			if (hasUserMessage) {
				const seen = new Set<string>();
				const deduped = restoredMessages.filter(m => {
					if (seen.has(m.id)) return false;
					seen.add(m.id);
					return true;
				});
				setMessages(finalizeStreamingMessages(deduped));
			} else {
				clearAll();
			}
		}
	}, []);

	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages, isTyping]);

	useEffect(() => {
		if (messages.length > 0) {
			saveMessages(messages);
		}
	}, [messages]);

	useEffect(() => {
		if (isOpen) {
			setTimeout(() => {
				messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
			}, 100);
		}
	}, [isOpen]);

	useEffect(() => {
		const listEl = messageListRef.current;
		if (!listEl) return;
		const handleScroll = () => {
			const distanceFromBottom = listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight;
			setShowScrollToBottom(distanceFromBottom > 100);
		};
		listEl.addEventListener('scroll', handleScroll);
		return () => listEl.removeEventListener('scroll', handleScroll);
	}, [isOpen, currentView]);

	const scrollToBottom = useCallback(() => {
		messageListRef.current?.scrollTo({
			top: messageListRef.current.scrollHeight,
			behavior: 'smooth',
		});
	}, []);

	const addBotMessage = (text: string, enableTyping: boolean = false) => {
		const newMessage: Message = {
			id: generateMessageId('bot'),
			text: enableTyping ? '' : text,
			sender: 'bot',
			timestamp: Date.now() / 1000,
			isTyping: enableTyping,
			fullContent: text,
		};
		setMessages((prev) => [...prev, newMessage]);
		if (enableTyping) {
			typeMessage(newMessage.id, text);
		}
	};

	const typeMessage = (messageId: string, fullText: string) => {
		const words = fullText.split(' ');
		let currentWordIndex = 0;
		const typeNextWord = () => {
			if (currentWordIndex <= words.length) {
				const currentText = words.slice(0, currentWordIndex).join(' ');
				setMessages((prev) =>
					prev.map((msg) =>
						msg.id === messageId
							? { ...msg, text: currentText, isTyping: currentWordIndex < words.length, fullContent: fullText }
							: msg
					)
				);
				currentWordIndex++;
				if (currentWordIndex <= words.length) {
					setTimeout(typeNextWord, Math.floor(Math.random() * 161) + 40);
				} else {
					setMessages((prev) =>
						prev.map((msg) =>
							msg.id === messageId ? { ...msg, text: fullText, isTyping: false, fullContent: fullText } : msg
						)
					);
				}
			}
		};
		typeNextWord();
	};

	const addUserMessage = (text: string) => {
		const newMessage: Message = {
			id: generateMessageId('user'),
			text,
			sender: 'user',
			timestamp: Date.now() / 1000,
		};
		setMessages((prev) => [...prev, newMessage]);
		logger.info('User sent message', { messageLength: text.length });
	};

	const handleSendMessage = async () => {
		if (!inputValue.trim()) return;
		const userInput = inputValue.trim();
		addUserMessage(userInput);
		setInputValue('');
		setShowSuggestions(false);
		setIsTyping(true);

		try {
			await sendToAgent(userInput);
		} catch (error: any) {
			logger.error('Error in handleSendMessage:', { error: error.message || error, details: error.details });
			const displayMessage = error.userMessage || 'I ran into an issue processing your request. Please try again.';
			addBotMessage(displayMessage, true);
			setIsTyping(false);
		}
	};

	const handleSuggestionClick = (suggestion: string) => {
		setInputValue(suggestion);
		inputRef.current?.focus();
	};

	const handleNewConversation = () => {
		if (messages.length > 1) {
			const newHistory: ConversationHistory = {
				id: generateMessageId('conv'),
				title: messages[1]?.text.substring(0, 50) || 'New Conversation',
				timestamp: Date.now() / 1000,
				messages: [...messages],
			};
			setConversationHistory((prev) => [newHistory, ...prev]);
			logger.info('Conversation saved to history');
		}
		resetSession();
		setMessages([]);
		setShowSuggestions(true);
	};

	const handleLoadConversation = (conversation: ConversationHistory) => {
		setMessages(conversation.messages);
		setCurrentView('chat');
		setSidebarOpen(false);
		logger.info('Loaded conversation from history');
	};

	const toggleOpen = () => {
		setIsOpen(!isOpen);
		logger.info(isOpen ? 'Chatbot closed' : 'Chatbot opened');
	};

	const showWelcome = messages.filter(m => m.sender === 'user').length === 0;

	return {
		isIconHovered,
		isOpen,
		messages,
		inputValue,
		isTyping,
		showSuggestions,
		currentView,
		sidebarOpen,
		conversationHistory,
		buttonPosition,
		isDragging,
		chatPanelRef,
		messagesEndRef,
		messageListRef,
		inputRef,
		suggestions: DEFAULT_SUGGESTIONS,
		userPhotoUrl,
		username,
		panelWidth,
		panelPositionStyle,
		isDarkMode,
		showWelcome,
		showScrollToBottom,

		setIsIconHovered,
		setIsOpen,
		setInputValue,
		setCurrentView,
		setSidebarOpen,

		handleSendMessage,
		handleSuggestionClick,
		handleNewConversation,
		handleLoadConversation,
		toggleOpen,
		handleMouseDown,
		handlePanelResize,
		handlePanelResizeEnd,
		scrollToBottom,
		stopResponse,
	};
};
